import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import classNames from 'classnames'
import './input.css'
import Button from '../button'
import { DAC } from '../interface'

export type TSize = 'S' | 'M' | 'L';

export interface InputProps {
  /**
   * input容器层样式名前缀
   */
  prefixClass?: string;
  wrapClassName?: string;
  className?: string;
  placeholder?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  // TODO
  // onChange?: React.FocusEventHandler<HTMLInputElement>;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  wrapStyle?: React.CSSProperties;
  style?: React.CSSProperties,
  clearIcon?: React.ReactNode;
  /**
   * @deprecated
   * @description 已废弃 clearIcon 代替
   */
  allowClear?: boolean;
  disabled?: boolean;
  value?: string | number;
  /**
   * @deprecated
   * @description 已废弃 onChange中的isClear代替
   */
  onClear?: React.MouseEventHandler<HTMLSpanElement>;
  maxLength?: number;
  size?: TSize;
  onChange?(ev: DAC.Event & { isClear?: boolean }): void;
  type?: React.HTMLInputTypeAttribute;
  max?: number;
  min?: number;
  onCompositionStart?: React.CompositionEventHandler<HTMLInputElement>;
  onCompositionEnd?: React.CompositionEventHandler<HTMLInputElement>;
  composition?: boolean;
  readOnly?: boolean;
  instance?: React.Ref<{ mutate: (v?: string) => void }>;
  /**
   * @description 显示长度限制，需和maxLength共同使用
   */
  limit?: boolean;
}

const Input = forwardRef<unknown, InputProps>((props, ref) => {
  const {
    prefixClass,
    wrapClassName,
    className,
    placeholder = '请输入',
    onFocus,
    onBlur,
    onChange,
    disabled,
    prefix,
    suffix,
    wrapStyle,
    allowClear = false,
    onClear,
    value: valueProp = '',
    size = 'M',
    instance,
    type,
    readOnly,
    composition,
    clearIcon,
    max,
    min,
    onCompositionStart,
    onCompositionEnd,
    onKeyDown,
    limit,
    maxLength,
    ...inputProps
  } = props

  const [isFocus, setFocus] = useState(false)
  const [value, setValue] = useState(valueProp)
  const [lock, setLock] = useState(false)

  useImperativeHandle(instance, () => ({
    mutate: (v?: string) => setValue(v || valueProp),
  }))

  useEffect(() => {
    if (valueProp !== value) {
      setValue(valueProp)
    }
  }, [valueProp])

  const focus = (ev: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocus(true)
    onFocus?.(ev)
  }

  const blur = (ev: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocus(false)
    onBlur?.(ev)
  }

  const clear = (ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    setValue('')
    if (onClear) {
      onClear?.(ev)
    } else {
      onChange?.({ value: '', event: ev, isClear: true })
    }
  }

  const onInput = (ev: React.FormEvent<HTMLInputElement>) => {
    let v = ev.currentTarget.value
    if (type === 'number') {
      if ((min || min === 0) && !!v && Number(v) < min) {
        v = min.toString()
      }
      if ((max || max === 0) && !!v && Number(v) > max) {
        v = max.toString()
      }
    }
    setValue(v)
    onChange?.({ value: v, event: ev })
  }

  return (
    <span
      className={classNames(`${prefixClass}InputWrap`, wrapClassName)}
      style={wrapStyle}
      data-size={size}
      data-focus={!!isFocus}
      data-disabled={!!disabled}
      data-clearable={
        (allowClear || clearIcon !== false) && !disabled && !!value
      }
      data-active={!!value}
    >
      {prefix}
      <input
        ref={ref as React.MutableRefObject<HTMLInputElement | null>}
        className={classNames(`${prefixClass}Input`, className)}
        {...inputProps}
        maxLength={maxLength}
        disabled={disabled}
        type={type}
        value={value}
        readOnly={readOnly}
        onInput={onInput}
        onKeyDown={(...args) => {
          if (!composition || !lock) {
            onKeyDown?.(...args)
          }
        }}
        onFocus={focus}
        onBlur={blur}
        placeholder={placeholder}
        onCompositionStart={(...args) => {
          setLock(true)
          onCompositionStart?.(...args)
        }}
        onCompositionEnd={(...args) => {
          setLock(false)
          onCompositionEnd?.(...args)
        }}
      />
      {(allowClear || clearIcon !== false) && !disabled && value && (
        <div onClick={clear} className={`${prefixClass}InputClear`}>
          {clearIcon || (
            <Button
              flat
              size="custom"
              style={{ display: 'flex', padding: 'calc(1em - 9px)' }}
            >
              <svg
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                className={`${prefixClass}InputClearIcon`}
              >
                <path d="M512 64a448 448 0 1 1 0 896A448 448 0 0 1 512 64zM370.176 325.696a32 32 0 0 0-40.832 48.96L466.752 512l-137.408 137.344-3.648 4.48a32 32 0 0 0 48.96 40.832L512 557.248l137.344 137.408 4.48 3.648a32 32 0 0 0 40.832-48.96L557.248 512l137.408-137.344 3.648-4.48a32 32 0 0 0-48.96-40.832L512 466.752 374.656 329.344z" />
              </svg>
            </Button>
          )}
        </div>
      )}
      {limit && maxLength && (
        <div className={`${prefixClass}InputLimit`}>
          {(value || value === 0 ? value.toString() : '')?.length}/
          {maxLength}
        </div>
      )}
      {suffix}
    </span>
  )
})

Input.defaultProps = {
  prefixClass: 'zy',
  placeholder: '请输入内容',
  composition: false,
}

export default Input
